import { useAuth0 } from '@auth0/auth0-react';
import {
  ActionIcon,
  Box,
  Group,
  Image,
  Loader,
  Popover,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft, IconPhoto, IconSearch, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { searchGameDbGames } from '../../api/gamedb';
import { useSystemStore } from '../../stores/systemStore';
import { selectPreferredCover } from '../../utils/coverUtils';
import { getOptimizedImageUrl } from '../../utils/imageUtils';
import { formatPlatformDisplay } from '../../utils/platformUtils';

export default function GlobalSearch() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { platforms, fetchSystems } = useSystemStore();
  const isMobile = useMediaQuery('(max-width: 62em)');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchSystems/getAccessTokenSilently are intentionally excluded
  useEffect(() => {
    fetchSystems(getAccessTokenSilently);
  }, []);

  const { data: results, isLoading } = useQuery({
    queryKey: ['globalSearch', debouncedSearch],
    queryFn: () => searchGameDbGames(getAccessTokenSilently, debouncedSearch),
    enabled: debouncedSearch.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const resetState = useCallback(() => {
    setSearchTerm('');
    setDropdownOpened(false);
    setMobileExpanded(false);
    setHighlightedIndex(-1);
  }, []);

  // Reset on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetState is intentionally excluded
  useEffect(() => {
    resetState();
  }, [location.pathname]);

  // Open dropdown when we have results
  useEffect(() => {
    if (debouncedSearch.length >= 2) {
      setDropdownOpened(true);
      setHighlightedIndex(-1);
    } else {
      setDropdownOpened(false);
    }
  }, [debouncedSearch]);

  const getCoverUrl = (game: any): string | null => {
    return selectPreferredCover(game.images)?.url || null;
  };

  const handleSelect = (gameId: string) => {
    navigate(`/gamedb/${gameId}`);
    resetState();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!results?.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightedIndex].id);
    } else if (e.key === 'Escape') {
      resetState();
    }
  };

  const renderResults = () => {
    if (isLoading && debouncedSearch.length >= 2) {
      return (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      );
    }

    if (!isLoading && debouncedSearch.length >= 2 && results && results.length === 0) {
      return (
        <Text c="dimmed" ta="center" py="md" size="sm">
          No games found
        </Text>
      );
    }

    return results?.map((game: any, index: number) => {
      const coverUrl = getCoverUrl(game);
      return (
        <UnstyledButton
          key={game.id}
          onClick={() => handleSelect(game.id)}
          onMouseEnter={() => setHighlightedIndex(index)}
          p="xs"
          style={{
            display: 'block',
            width: '100%',
            borderRadius: 'var(--mantine-radius-sm)',
            backgroundColor: index === highlightedIndex ? 'var(--mantine-color-gray-1)' : 'transparent',
          }}
        >
          <Group wrap="nowrap" gap="sm">
            {coverUrl ? (
              <Image
                src={getOptimizedImageUrl(coverUrl, 'igdb', 'cover_small')}
                alt={game.name}
                w={45}
                h={60}
                radius="sm"
                fit="cover"
              />
            ) : (
              <Group
                w={45}
                h={60}
                justify="center"
                align="center"
                bg="gray.1"
                style={{ borderRadius: 'var(--mantine-radius-sm)' }}
              >
                <IconPhoto size={18} color="var(--mantine-color-gray-5)" />
              </Group>
            )}
            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" fw={500} lineClamp={1}>
                {game.name}
                {game.firstReleaseDate && (
                  <Text span c="dimmed" size="xs">
                    {' '}
                    ({new Date(game.firstReleaseDate).getFullYear()})
                  </Text>
                )}
              </Text>
              {game.platforms && game.platforms.length > 0 && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {formatPlatformDisplay(game.platforms, platforms)}
                </Text>
              )}
            </Stack>
          </Group>
        </UnstyledButton>
      );
    });
  };

  const searchInput = (autoFocus?: boolean) => (
    <TextInput
      placeholder="Search 50+ years of games..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.currentTarget.value)}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (debouncedSearch.length >= 2) {
          setDropdownOpened(true);
        }
      }}
      leftSection={<IconSearch size={16} />}
      rightSection={
        searchTerm ? (
          <ActionIcon variant="subtle" onClick={resetState} size="sm">
            <IconX size={16} />
          </ActionIcon>
        ) : null
      }
      size="sm"
      autoFocus={autoFocus}
      styles={{
        input: {
          backgroundColor: 'var(--mantine-color-default)',
        },
      }}
    />
  );

  // Mobile: expanded overlay (covers header)
  if (isMobile && mobileExpanded) {
    return (
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          backgroundColor: 'var(--mantine-color-body)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--mantine-spacing-xs)',
          gap: 'var(--mantine-spacing-xs)',
        }}
      >
        <ActionIcon variant="subtle" onClick={resetState} size="lg">
          <IconArrowLeft size={20} />
        </ActionIcon>
        <Popover
          opened={dropdownOpened}
          onChange={setDropdownOpened}
          width="target"
          position="bottom"
          shadow="md"
          middlewares={{ flip: false, shift: true }}
        >
          <Popover.Target>
            <Box style={{ flex: 1 }}>{searchInput(true)}</Box>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            <ScrollArea.Autosize mah={400}>{renderResults()}</ScrollArea.Autosize>
          </Popover.Dropdown>
        </Popover>
      </Box>
    );
  }

  // Mobile: collapsed — inline search field in normal header flow
  if (isMobile) {
    return (
      <Box style={{ flex: 1, minWidth: 0 }}>
        <TextInput
          placeholder="Search 50+ years of games..."
          value={searchTerm}
          onFocus={() => setMobileExpanded(true)}
          readOnly
          leftSection={<IconSearch size={16} />}
          size="sm"
          styles={{
            input: {
              backgroundColor: 'var(--mantine-color-default)',
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    );
  }

  // Desktop
  return (
    <Box style={{ flex: 1, maxWidth: 480 }}>
      <Popover
        opened={dropdownOpened}
        onChange={setDropdownOpened}
        width="target"
        position="bottom"
        shadow="md"
        middlewares={{ flip: false, shift: true }}
      >
        <Popover.Target>{searchInput()}</Popover.Target>
        <Popover.Dropdown p="xs">
          <ScrollArea.Autosize mah={400}>{renderResults()}</ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
